import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import NewsletterCard from '../../components/newsletter/NewsletterCard';
import NewsletterWideCard from '../../components/newsletter/NewsletterWideCard';
import {
  NewsletterItem,
  NewsletterPageContent,
  DEFAULT_PAGE_CONTENT,
  fetchNewsletters,
  fetchNewsletterPageContent,
} from '../../services/newsletterApi';

/**
 * 뉴스레터 홈 (Figma 0.Hi-Fi > Newsletter, node 6460:61539)
 *
 * Container 1200px / 섹션 간 gap 128 / 하단 padding 128
 *   1. Banner    height 480, radius 12
 *   2. Featured  row gap 24 (타이틀 + Card/NewsletterWide 690)
 *   3. Subscription  row, padding 96 48, gap 40
 *   4. List      column gap 24, 한 행에 카드 2장 (588 + 24 + 588 = 1200)
 */

const NewsletterHome: React.FC = () => {
  const [content, setContent] = useState<NewsletterPageContent>(DEFAULT_PAGE_CONTENT);
  const [newsletters, setNewsletters] = useState<NewsletterItem[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      const [pageContent, items] = await Promise.all([
        fetchNewsletterPageContent(),
        fetchNewsletters(),
      ]);

      if (!mounted) return;
      setContent(pageContent);
      setNewsletters(items);
      setInitialLoading(false);
    };

    load();

    return () => {
      mounted = false;
    };
  }, []);

  const handleSubscribe = () => {
    if (!content.subscription_url || content.subscription_url === '#') return;
    window.open(content.subscription_url, '_blank', 'noopener,noreferrer');
  };

  return (
    <LayoutContainer>
      <Header />
      <MainContent>
        <ContentArea>
          <PageColumn>
            {/* 1. 배너 — 배경 이미지는 화면 전체 폭, 텍스트만 1200 컨테이너 정렬 */}
            <BannerBand $imageUrl={content.banner_image_url}>
              <BannerInner>
                <BannerEyebrow $color={content.banner_font_color}>{content.banner_eyebrow}</BannerEyebrow>
                <BannerTitle $color={content.banner_font_color}>{content.banner_title}</BannerTitle>
              </BannerInner>
            </BannerBand>

            {/* 2. Featured */}
            <Section1200>
              <FeaturedSection>
                <FeaturedTitleBox>
                  <FeaturedLabel>{content.featured_label}</FeaturedLabel>
                  <FeaturedTitle>{content.featured_title}</FeaturedTitle>
                </FeaturedTitleBox>
                <NewsletterWideCard
                  imageUrl={content.featured_image_url}
                  title={content.featured_overlay_title}
                  caption={content.featured_overlay_caption}
                  href={content.featured_url}
                />
              </FeaturedSection>
            </Section1200>

            {/* 3. 구독 CTA — 민트 배경은 화면 전체 폭, 내용만 1200 컨테이너 정렬 */}
            <SubscriptionBand>
              <SubscriptionInner>
                <SubscriptionTitle>{content.subscription_title}</SubscriptionTitle>
                <SubscribeButton type="button" onClick={handleSubscribe}>
                  {content.subscription_button_text}
                  {/* Icon/Normal/ChevronRightThick — Small=true, Thick=true */}
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M7.5 4.5L13 10L7.5 15.5"
                      stroke="#FFFFFF"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </SubscribeButton>
              </SubscriptionInner>
            </SubscriptionBand>

            {/* 4. 카드 목록 */}
            <Section1200>
              <ListSection>
                {initialLoading ? (
                  <LoadingContainer>
                    <LoadingSpinner />
                  </LoadingContainer>
                ) : newsletters.length === 0 ? (
                  <EmptyContainer>등록된 뉴스레터가 없습니다.</EmptyContainer>
                ) : (
                  <CardGrid>
                    {newsletters.map((item) => (
                      <NewsletterCard key={item.newsletter_id} item={item} />
                    ))}
                  </CardGrid>
                )}
              </ListSection>
            </Section1200>
          </PageColumn>
        </ContentArea>
      </MainContent>
      <Footer />
    </LayoutContainer>
  );
};

export default NewsletterHome;

const LayoutContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-width: 320px;
  overflow-x: hidden;
`;

const MainContent = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
  position: relative;
  min-height: calc(100vh - 80px);
  align-items: flex-start;
  justify-content: center;
`;

const ContentArea = styled.main`
  width: 100%;
  min-height: calc(100vh - 80px);
  background: white;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`;

/**
 * 배너와 구독 섹션은 배경이 화면 전체 폭을 차지하고(라운드 없음),
 * 내용만 1200 컨테이너에 정렬됩니다. 그래서 1200 래퍼를 페이지 전체가 아니라
 * 섹션별로 둡니다.
 */
const PageColumn = styled.div`
  width: 100%;
  padding-top: 80px; /* 고정 헤더 높이 — 배너가 GNB에 바로 붙습니다 */
  padding-bottom: 128px;
  display: flex;
  flex-direction: column;
  gap: 128px;

  @media (max-width: 768px) {
    padding-bottom: 80px;
    gap: 80px;
  }
`;

const Section1200 = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 1280px) {
    padding-left: 40px;
    padding-right: 40px;
  }

  @media (max-width: 768px) {
    padding-left: 20px;
    padding-right: 20px;
  }
`;

/* ---------------------------------------------------------------- 1. 배너 */

const BannerBand = styled.section<{ $imageUrl: string }>`
  position: relative;
  width: 100%;
  height: 480px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background-image: ${({ $imageUrl }) => `url(${$imageUrl})`};
  background-size: cover;
  background-position: center;
  background-color: #f5f5f5;

  @media (max-width: 768px) {
    height: 320px;
  }
`;

const BannerInner = styled.div`
  position: relative;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (max-width: 1280px) {
    padding-left: 40px;
    padding-right: 40px;
  }

  @media (max-width: 768px) {
    padding-left: 20px;
    padding-right: 20px;
  }
`;

/**
 * 피그마 배너에는 스크림이 없습니다(이미지 한 장).
 * 다만 배너 이미지는 백오피스에서 임의로 교체되므로 밝은 사진이 올라오면
 * 흰 텍스트가 읽히지 않습니다. Card/NewsletterWide 가 쓰는 검정 16%와
 * 같은 값으로 최소한만 얹었습니다. 디자인 확인 후 제거 가능합니다.
 */
/* Heading 1/Regular — Pretendard JP 400 / 22px / 136% / -1.94% */
const BannerEyebrow = styled.span<{ $color: string }>`
  color: ${({ $color }) => $color};
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  font-size: 22px;
  line-height: 1.36;
  letter-spacing: -0.0194em;

  @media (max-width: 768px) {
    font-size: 17px;
  }
`;

/* Display 1/Bold — Pretendard JP 700 / 56px / 129% / -3.19% */
const BannerTitle = styled.h1<{ $color: string }>`
  margin: 0;
  color: ${({ $color }) => $color};
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  font-size: 56px;
  line-height: 1.29;
  letter-spacing: -0.0319em;
  white-space: pre-line;

  @media (max-width: 1024px) {
    font-size: 44px;
  }

  @media (max-width: 768px) {
    font-size: 30px;
  }
`;

/* ------------------------------------------------------- 2~4. 콘텐츠 섹션 */

const FeaturedSection = styled.section`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 24px;
  align-self: stretch;

  @media (max-width: 1280px) {
    flex-direction: column;
    align-items: stretch;
    gap: 32px;
  }
`;

const FeaturedTitleBox = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 12px;
`;

const FeaturedLabel = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  font-size: 17px;
  line-height: 1.41em;
`;

const FeaturedTitle = styled.h2`
  margin: 0;
  color: #000000;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  font-size: 40px;
  line-height: 1.3em;
  letter-spacing: -0.0282em;
  white-space: pre-line;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

/* 구독 CTA */

/* Background 노드가 x:-120 / width 1440 으로 1200 프레임 밖까지 깔립니다 — Atomic/To Be Doctor/99 */
const SubscriptionBand = styled.section`
  width: 100%;
  background: #f0f7f7;
`;

const SubscriptionInner = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 40px;
  padding: 96px 48px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 48px 24px;
    gap: 24px;
  }
`;

/* Title 1/Bold — Pretendard JP 700 / 36px / 133% / -2.7%, Semantic/Primary/Strong */
const SubscriptionTitle = styled.p`
  margin: 0;
  color: #296768;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  font-size: 36px;
  line-height: 1.33;
  letter-spacing: -0.027em;
  white-space: pre-line;

  @media (max-width: 1024px) {
    font-size: 28px;
  }

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

/**
 * 디자인 시스템 Button/Solid (Variant=Primary, Size=Large, Trailing Icon=true)
 * 198 × 48 hug / radius 12 / padding 12 28 / Semantic/Primary/Normal #448181
 * 라벨: Body 1/Normal - Bold — Pretendard JP 600 / 16px / 150% / 0.57%
 *
 * 공용 common/Button 의 large 프리셋(18px·500·padding 12 24)은 이 DS 스펙과
 * 달라서 그대로 쓰지 못했습니다. 공용 컴포넌트를 고치면 다른 페이지에 전부
 * 영향이 가므로 여기서는 페이지 전용으로 두고 별도 이슈로 남깁니다.
 */
const SubscribeButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 48px;
  padding: 12px 28px;
  border: none;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.primary};
  color: #ffffff;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 1.5;
  letter-spacing: 0.0057em;
  white-space: nowrap;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
  }
`;

/* 카드 목록 */

const ListSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-self: stretch;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 80px 0;
`;

const EmptyContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 80px 0;
  color: ${({ theme }) => theme.colors.textLight};
  font-family: 'Pretendard JP', sans-serif;
  font-size: 16px;
`;
